import { api, ApiError } from '../../lib/api';
import type {
  PlanDashboard,
  PromotionPlan,
  PromotionPlanStatus,
  PromotionPlanTask,
  PromotionPlanTaskStatus,
} from '../../types/promotionPlan';
import type { Campaign } from '../../types/campaign';
import type { Rollout } from '../../types/rollout';
import type { CurrentUser } from '../../types/user';
import type {
  TaskTemplate,
  TaskTemplateWithItems,
} from '../../types/taskTemplate';
import type { AuditTimelineEntry } from '../../types/auditTimeline';
import type { PromotionPlanThreadEntry } from '../../types/promotionPlanThread';

// Client API for the plans module. Mirrors campaigns/api.ts + rollouts/api.ts
// conventions (thin fetch wrappers, 404 -> null via `maybe`). Imported only
// by components inside src/modules/plans/.

export type PlanCreatePayload = {
  name: string;
  brandAccountId: string;
  status?: PromotionPlanStatus;
  overview?: string | null;
  offerMechanicsUrl: string | null;
  kyckCampaignOverviewUrl: string | null;
  promoStartDate: string | null; // ISO datetime (Consolidation §1.1)
  promoEndDate: string | null;
  rolloutSlug?: string | null;
  monthYearLabel?: string | null;
  notes?: string | null;
  // Always null on create (no Campaigns attached yet); server enforces.
  defaultCampaignId?: string | null;
};

// PATCH endpoint. brandAccountId is immutable post-create; status moves
// through its own endpoint -- both are intentionally omitted here.
export type PlanUpdatePayload = Partial<{
  name: string;
  overview: string | null;
  offerMechanicsUrl: string | null;
  kyckCampaignOverviewUrl: string | null;
  promoStartDate: string | null;
  promoEndDate: string | null;
  rolloutSlug: string | null;
  monthYearLabel: string | null;
  notes: string | null;
  defaultCampaignId: string | null;
}>;

async function maybe<T>(fn: () => Promise<T>): Promise<T | null> {
  try {
    return await fn();
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

export async function listPlans(
  options: { brandAccountId?: string; status?: PromotionPlanStatus } = {},
): Promise<PromotionPlan[]> {
  const params = new URLSearchParams();
  if (options.brandAccountId) params.set('brandAccountId', options.brandAccountId);
  if (options.status) params.set('status', options.status);
  const qs = params.size > 0 ? `?${params.toString()}` : '';
  const res = await api<{ plans: PromotionPlan[] }>(`/api/plans${qs}`);
  return res.plans;
}

export async function getPlan(id: string): Promise<PromotionPlan | null> {
  return maybe(async () => {
    const res = await api<{ plan: PromotionPlan }>(`/api/plans/${id}`);
    return res.plan;
  });
}

export async function createPlan(payload: PlanCreatePayload): Promise<PromotionPlan> {
  const res = await api<{ plan: PromotionPlan }>('/api/plans', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return res.plan;
}

export async function updatePlan(
  id: string,
  patch: PlanUpdatePayload,
): Promise<PromotionPlan> {
  const res = await api<{ plan: PromotionPlan }>(`/api/plans/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return res.plan;
}

export async function changePlanStatus(
  id: string,
  status: PromotionPlanStatus,
): Promise<PromotionPlan> {
  const res = await api<{ plan: PromotionPlan }>(`/api/plans/${id}/status`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  });
  return res.plan;
}

// HARD delete (Deletion spec) — permanent + irreversible. Cascades to the
// Plan's tasks server-side. Soft-archive is a status change (changePlanStatus
// → 'archived'), NOT this. Server 409s if a Rollout is still attached.
export type HardDeletePlanResult = {
  deletedPlanId: string;
  deletedTaskCount: number;
};

export async function hardDeletePlan(
  id: string,
): Promise<HardDeletePlanResult> {
  return api<HardDeletePlanResult>(`/api/plans/${id}`, { method: 'DELETE' });
}

// Campaign attachment (Slice C §2) -------------------------------------------

export async function attachCampaign(
  planId: string,
  campaignId: string,
): Promise<PromotionPlan> {
  const res = await api<{ plan: PromotionPlan }>(
    `/api/plans/${planId}/campaigns/${campaignId}`,
    { method: 'POST', body: '{}' },
  );
  return res.plan;
}

export async function detachCampaign(
  planId: string,
  campaignId: string,
): Promise<PromotionPlan> {
  const res = await api<{ plan: PromotionPlan }>(
    `/api/plans/${planId}/campaigns/${campaignId}`,
    { method: 'DELETE' },
  );
  return res.plan;
}

export async function reorderCampaigns(
  planId: string,
  campaignIds: string[],
): Promise<PromotionPlan> {
  const res = await api<{ plan: PromotionPlan }>(
    `/api/plans/${planId}/campaigns/order`,
    { method: 'PATCH', body: JSON.stringify({ campaignIds }) },
  );
  return res.plan;
}

// Campaigns visible to the Plan's brand, for the attach picker (spec §2.2).
// Hits the shared /api/campaigns endpoint directly rather than importing the
// campaigns module's api.ts — same module-boundary discipline as listUsers
// below (each client module owns its api.ts). filter defaults to
// 'visibility' server-side, which is the "assignable under this brand" set.
export async function listBrandCampaigns(
  brandAccountId: string,
): Promise<Campaign[]> {
  const res = await api<{ campaigns: Campaign[] }>(
    `/api/campaigns?brandAccountId=${encodeURIComponent(brandAccountId)}`,
  );
  return res.campaigns;
}

// Plan → Rollout (Consolidation §3) ------------------------------------------

// Single-click: no payload. Every Rollout field is read from the Plan
// server-side. Returns the now-connected Plan (rolloutId + datesLocked
// set) alongside the freshly-spawned Rollout so the caller can jump
// straight to its grid.
export async function createRolloutFromPlan(
  planId: string,
): Promise<{ plan: PromotionPlan; rollout: Rollout }> {
  return api<{ plan: PromotionPlan; rollout: Rollout }>(
    `/api/plans/${planId}/rollout`,
    { method: 'POST', body: '{}' },
  );
}

// Dashboard (Slice C §6) -----------------------------------------------------

// Real-time stats; refetched on focus by the detail page so a change made
// in the Rollout grid reflects on the Plan without a hard reload.
export async function getPlanDashboard(
  planId: string,
): Promise<PlanDashboard> {
  const res = await api<{ dashboard: PlanDashboard }>(
    `/api/plans/${planId}/dashboard`,
  );
  return res.dashboard;
}

// Apply task template (Slice B §4) ------------------------------------------

// Templates visible to this Plan's brand for the apply picker: Global +
// this brand's only (§2.3). Hits the shared /api/task-templates endpoint
// directly — each client module owns its api.ts (same discipline as
// listBrandCampaigns / listUsers above; the settings module owns the
// template-management wrappers, plans owns the apply-side ones).
export async function listTemplatesForBrand(
  brandAccountId: string,
): Promise<TaskTemplate[]> {
  const res = await api<{ templates: TaskTemplate[] }>(
    `/api/task-templates?forBrandAccountId=${encodeURIComponent(
      brandAccountId,
    )}`,
  );
  return res.templates;
}

// Template + its ordered items, for the apply modal's resolved-date
// preview (§4.2). 404 -> null (template deleted between list and open).
export async function getTemplateForApply(
  templateId: string,
): Promise<TaskTemplateWithItems | null> {
  return maybe(async () => {
    const res = await api<{ template: TaskTemplateWithItems }>(
      `/api/task-templates/${templateId}`,
    );
    return res.template;
  });
}

export type ApplyTemplateResult = {
  appliedTemplateName: string;
  createdTaskCount: number;
};

export async function applyTemplate(
  planId: string,
  templateId: string,
): Promise<ApplyTemplateResult> {
  return api<ApplyTemplateResult>(
    `/api/plans/${planId}/apply-template/${templateId}`,
    { method: 'POST', body: '{}' },
  );
}

// Activity feed (Slice B §6) -------------------------------------------------

export type PlanActivityFilter = 'all' | 'tasks' | 'mine';

export async function getPlanActivity(
  planId: string,
  filter: PlanActivityFilter = 'all',
): Promise<AuditTimelineEntry[]> {
  const qs = filter === 'all' ? '' : `?filter=${filter}`;
  const res = await api<{ entries: AuditTimelineEntry[] }>(
    `/api/plans/${planId}/activity${qs}`,
  );
  return res.entries;
}

// Plan thread (Slice B §7) ---------------------------------------------------

export async function listThread(
  planId: string,
): Promise<PromotionPlanThreadEntry[]> {
  const res = await api<{ entries: PromotionPlanThreadEntry[] }>(
    `/api/plans/${planId}/thread`,
  );
  return res.entries;
}

export async function postComment(
  planId: string,
  content: string,
): Promise<PromotionPlanThreadEntry> {
  const res = await api<{ entry: PromotionPlanThreadEntry }>(
    `/api/plans/${planId}/thread`,
    { method: 'POST', body: JSON.stringify({ content }) },
  );
  return res.entry;
}

export async function editComment(
  planId: string,
  entryId: string,
  content: string,
): Promise<PromotionPlanThreadEntry> {
  const res = await api<{ entry: PromotionPlanThreadEntry }>(
    `/api/plans/${planId}/thread/${entryId}`,
    { method: 'PATCH', body: JSON.stringify({ content }) },
  );
  return res.entry;
}

export async function deleteComment(
  planId: string,
  entryId: string,
): Promise<PromotionPlanThreadEntry> {
  const res = await api<{ entry: PromotionPlanThreadEntry }>(
    `/api/plans/${planId}/thread/${entryId}`,
    { method: 'DELETE' },
  );
  return res.entry;
}

// Tasks ----------------------------------------------------------------------

export type TaskCreatePayload = {
  title: string;
  description: string | null;
  notes: string | null;
  assignedUserId: string | null;
  assignedTeam?: string | null;
  dueDate: string | null;
  status?: PromotionPlanTaskStatus;
};

export type TaskUpdatePayload = Partial<{
  title: string;
  description: string | null;
  notes: string | null;
  assignedUserId: string | null;
  assignedTeam: string | null;
  dueDate: string | null;
  status: PromotionPlanTaskStatus;
  position: number;
}>;

export async function listTasks(planId: string): Promise<PromotionPlanTask[]> {
  const res = await api<{ tasks: PromotionPlanTask[] }>(
    `/api/plans/${planId}/tasks`,
  );
  return res.tasks;
}

export async function createTask(
  planId: string,
  payload: TaskCreatePayload,
): Promise<PromotionPlanTask> {
  const res = await api<{ task: PromotionPlanTask }>(
    `/api/plans/${planId}/tasks`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
  return res.task;
}

export async function updateTask(
  planId: string,
  taskId: string,
  patch: TaskUpdatePayload,
): Promise<PromotionPlanTask> {
  const res = await api<{ task: PromotionPlanTask }>(
    `/api/plans/${planId}/tasks/${taskId}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
  );
  return res.task;
}

export async function deleteTask(
  planId: string,
  taskId: string,
): Promise<void> {
  await api<{ ok: true }>(`/api/plans/${planId}/tasks/${taskId}`, {
    method: 'DELETE',
  });
}

// Lightweight user list for the task-assignee dropdown. Hits the shared
// /api/users endpoint directly rather than importing the rollouts module's
// copy (module-boundary discipline -- each client module owns its api.ts).
export async function listUsers(): Promise<CurrentUser[]> {
  const res = await api<{ users: CurrentUser[] }>('/api/users');
  return res.users;
}
